/**
 * ------------------------------------------------------------------------------------------------------------
 * STOREFRONT-HINWEIS (Land & Sprache)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Gehoert zu sections/localization-banner.liquid. Vergleicht die aufgerufene Storefront mit dem, was der Browser
 * des Besuchers nahelegt, und blendet ganz oben eine Leiste ein, wenn beides auseinanderfaellt: eine Frage und
 * zwei Antworten — links die aktuelle Region (bleiben), rechts die empfohlene (wechseln).
 *
 * Es wird NIE automatisch weitergeleitet. Der Wechsel passiert erst, wenn der Besucher den Knopf drueckt — dann
 * ueber das Standard-Formular 'localization', das Land und Sprache in einem POST setzt und dabei auf der
 * aktuellen Seite bleibt.
 *
 * ERKENNUNG: /browsing_context_suggestions.json — Land aus der Geo-IP, Sprache aus Accept-Language. Dasselbe
 * Endpoint nutzt Shopifys eigene Geolocation-App.
 *
 * WELCHE STOREFRONT RICHTIG WAERE, steht schon auf der Seite: Shopify rendert die hreflang-Alternates fuer jede
 * Kombination aus Sprache und Land (hier 475 Stueck). Deshalb entscheidet nicht ein Vergleich von Laendercodes,
 * ob jemand falsch ist, sondern ein Vergleich von URLs: fuehrt <link hreflang="de-AT"> woandershin als die
 * aktuelle Wurzel, ist es eine andere Storefront — fuehrt es an dieselbe Stelle, gibt es nichts zu wechseln.
 *
 * Das ist der Unterschied, der bei Maerkten mit mehreren Laendern zaehlt: Auf /en-eu/ meldet Shopify Belgien als
 * Land. Ein Besucher aus Frankreich hat damit ein "anderes" Land, aber dieselbe Storefront — hreflang="en-FR"
 * zeigt auf /en-eu, also schweigt der Hinweis. Ein Besucher aus Oesterreich auf /de-de/ hat dagegen mit
 * hreflang="de-AT" -> /de-at wirklich eine andere Storefront.
 *
 * Deutschland taucht in den Alternates nicht als "de-DE" auf, sondern als blosses "de" (Standardmarkt). Die
 * Suche faellt deshalb von "<sprache>-<land>" auf "<sprache>" zurueck.
 *
 * Die Entscheidung ("wechseln" oder "nein danke") wird je Storefront in localStorage gemerkt.
 */

(function () {
  'use strict';

  var STORAGE_PREFIX = 'mms:localization-banner:';

  /** 'en-DE' und 'en' sind dieselbe Sprache — verglichen wird immer nur das Basis-Subtag. */
  function baseLanguage(code) {
    return String(code || '').toLowerCase().split('-')[0];
  }

  /** '/de-de/' und '/de-de' sind dieselbe Storefront; die Wurzel bleibt '/'. */
  function normalizePath(path) {
    var normalized = String(path || '/').replace(/\/+$/, '');
    return normalized === '' ? '/' : normalized;
  }

  /**
   * Liest den Wert des localization-Cookies. Shopify setzt es pfadgebunden je Markt; sind mehrere sichtbar und
   * widersprechen sie sich, gewinnt der serverseitig gerenderte Wert.
   */
  function readLocalizationCookie() {
    var values = [];

    document.cookie.split(';').forEach(function (part) {
      var pair = part.trim().split('=');

      if (pair[0] === 'localization' && pair[1] && values.indexOf(pair[1]) === -1) {
        values.push(pair[1]);
      }
    });

    return values.length === 1 ? values[0].toUpperCase() : null;
  }

  /** Alle hreflang-Alternates der Seite als Karte 'de-at' -> '/de-at'. */
  function alternatePaths() {
    var map = {};

    document.querySelectorAll('link[rel="alternate"][hreflang][href]').forEach(function (link) {
      try {
        map[link.getAttribute('hreflang').toLowerCase()] = normalizePath(new URL(link.href, window.location.origin).pathname);
      } catch (error) {
        // Kaputtes href — ueberspringen.
      }
    });

    return map;
  }

  /**
   * Land- bzw. Sprachname in der Sprache des Hinweises. Intl.DisplayNames gibt es in allen aktuellen Browsern;
   * fehlt es, faellt die Anzeige auf den mitgelieferten Namen und zuletzt auf den Code zurueck.
   */
  function displayName(type, code, locale, fallback) {
    try {
      if (window.Intl && Intl.DisplayNames) {
        var name = new Intl.DisplayNames([locale], { type: type }).of(code);

        if (name && name !== code) {
          return name;
        }
      }
    } catch (error) {
      // Intl kennt den Code nicht — unten weiter.
    }

    return fallback || code;
  }

  function fillPlaceholders(template, values) {
    if (!template) {
      return '';
    }

    return template.replace(/@([a-z_]+)@/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  class LocalizationBanner extends HTMLElement {
    connectedCallback() {
      var configElement = this.querySelector('[data-localization-banner-config]');

      if (!configElement) {
        return;
      }

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        return;
      }

      this.form = this.querySelector('form');
      this.questionElement = this.querySelector('[data-banner-question]');
      this.orElement = this.querySelector('[data-banner-or]');
      this.stayButton = this.querySelector('[data-banner-stay]');
      this.switchButton = this.querySelector('[data-banner-switch]');
      this.countryInput = this.querySelector('[data-banner-country]');
      this.localeInput = this.querySelector('[data-banner-locale]');

      if (!this.form || !this.questionElement || !this.stayButton || !this.switchButton) {
        return;
      }

      this.stayButton.addEventListener('click', this._onStay.bind(this));
      this.form.addEventListener('submit', this._rememberDecision.bind(this));

      if (window.Shopify && window.Shopify.designMode) {
        this._setUpDesignMode();
        return;
      }

      if (this._hasDecided()) {
        return;
      }

      this._detect();
    }

    get currentCountry() {
      return readLocalizationCookie() || String(this.config.currentCountry || '').toUpperCase();
    }

    get currentLanguage() {
      return baseLanguage(this.config.currentLanguage);
    }

    get currentPath() {
      return normalizePath(this.config.rootUrl);
    }

    /** Wohin diese Kombination gehoert — 'de-at' zuerst, sonst der Standardmarkt unter 'de'. */
    _storefrontPath(alternates, language, country) {
      var exact = alternates[language + '-' + String(country).toLowerCase()];
      return exact || alternates[language] || null;
    }

    async _detect() {
      var url = this.currentPath.replace(/\/$/, '') +
        '/browsing_context_suggestions.json?country%5Benabled%5D=true&language%5Benabled%5D=true';
      var data;

      try {
        var response = await fetch(url, { headers: { Accept: 'application/json' } });

        if (!response.ok) {
          return;
        }

        data = await response.json();
      } catch (error) {
        return;
      }

      var suggestion = (data.suggestions || [])[0] || {};
      var parts = suggestion.parts || {};
      var detected = data.detected_values || {};

      var languages = (this.config.availableLanguages || []).map(baseLanguage);
      var currencies = this.config.currencyByCountry || {};
      var alternates = alternatePaths();

      // --- Sprache -------------------------------------------------------------------------------------------
      var suggestedLanguage = null;

      if (this.config.matchLanguage && parts.language && parts.language.handle) {
        var candidate = baseLanguage(parts.language.handle);

        if (languages.indexOf(candidate) !== -1) {
          suggestedLanguage = candidate;
        }
      }

      var languageMismatch = Boolean(suggestedLanguage && suggestedLanguage !== this.currentLanguage);

      // --- Land ----------------------------------------------------------------------------------------------
      var detectedCountry = null;

      if (this.config.matchCountry) {
        detectedCountry = (detected.country && detected.country.handle) ||
          (parts.country && parts.country.handle) || null;
      }

      if (detectedCountry && !Object.prototype.hasOwnProperty.call(currencies, detectedCountry)) {
        detectedCountry = null; // Land, in das wir nicht liefern.
      }

      // Entscheidend ist nicht der Laendercode, sondern ob er auf eine andere Storefront zeigt.
      var countryMismatch = false;

      if (detectedCountry && detectedCountry !== this.currentCountry) {
        var pathForDetected = this._storefrontPath(alternates, this.currentLanguage, detectedCountry);

        // Ohne hreflang-Karte bleibt nur der Vergleich der Codes.
        countryMismatch = pathForDetected === null ? true : pathForDetected !== this.currentPath;
      }

      if (!countryMismatch && !languageMismatch) {
        return;
      }

      this._render({
        countryMismatch: countryMismatch,
        languageMismatch: languageMismatch,
        targetCountry: countryMismatch ? detectedCountry : this.currentCountry,
        targetLanguage: languageMismatch ? suggestedLanguage : this.currentLanguage,
        countryNames: (parts.country && parts.country.options) || {}
      });
    }

    _render(context) {
      var bannerLocale = context.languageMismatch ? context.targetLanguage : this.currentLanguage;
      var allStrings = this.config.strings || {};
      var strings = allStrings[bannerLocale] || allStrings[this.currentLanguage];

      if (!strings) {
        return;
      }

      var currencies = this.config.currencyByCountry || {};
      var element = this;

      // Beide Knoepfe tragen dieselbe Form — links die aktuelle Wahl, rechts die empfohlene. Welche Angaben
      // darin stehen, richtet sich danach, was sich ueberhaupt unterscheidet: nur Region, nur Sprache, beides.
      function label(country, language) {
        var template = context.countryMismatch && context.languageMismatch
          ? strings.option_region_language
          : context.countryMismatch
            ? strings.option_region
            : strings.option_language;

        return fillPlaceholders(template, {
          country: displayName('region', country, bannerLocale, context.countryNames[country]),
          currency: currencies[country] || '',
          language: displayName('language', language, bannerLocale, null)
        }).replace(/\s*\(\s*\)/, ''); // Land ohne bekannte Waehrung: leere Klammer wieder weg.
      }

      this.questionElement.textContent = context.countryMismatch ? strings.question_region : strings.question_language;
      this.orElement.textContent = strings.or;
      this.stayButton.textContent = label(this.currentCountry, this.currentLanguage);
      this.switchButton.textContent = label(context.targetCountry, context.targetLanguage);

      this.countryInput.value = context.targetCountry;
      this.localeInput.value = this._publishedLocale(context.targetLanguage);

      // Die Leiste spricht die Zielsprache und nicht die der Seite — das muss am Element stehen.
      this.setAttribute('lang', bannerLocale);
      this.setAttribute('aria-label', strings.region_label || '');
      this.hidden = false;
    }

    /** Das Formular braucht einen veroeffentlichten Locale-Code, nicht das blosse Basis-Subtag. */
    _publishedLocale(language) {
      var match = (this.config.availableLanguages || []).filter(function (code) {
        return baseLanguage(code) === language;
      })[0];

      return match || language;
    }

    /** Der linke Knopf ist die aktuelle Region: dranbleiben, und nicht wieder gefragt werden. */
    _onStay() {
      this._rememberDecision();
      this.hidden = true;
    }

    get _storageKey() {
      return STORAGE_PREFIX + this.currentPath;
    }

    _hasDecided() {
      try {
        return window.localStorage.getItem(this._storageKey) !== null;
      } catch (error) {
        return false;
      }
    }

    _rememberDecision() {
      try {
        window.localStorage.setItem(this._storageKey, '1');
      } catch (error) {
        // Privater Modus oder Speicher voll — dann erscheint der Hinweis eben erneut.
      }
    }

    /**
     * Im Theme-Editor gibt es weder Geo-IP-Wechsel noch Sinn im Gemerkten. Stattdessen zeigen wir den Hinweis
     * mit der jeweils anderen Sprache, sobald die Section ausgewaehlt wird — so laesst er sich uebersetzen und
     * gestalten, ohne die Vorschau dauerhaft zu verstellen.
     */
    _setUpDesignMode() {
      var section = this.closest('.shopify-section');
      var element = this;

      document.addEventListener('shopify:section:select', function (event) {
        if (event.target !== section) {
          return;
        }

        var other = (element.config.availableLanguages || []).map(baseLanguage).filter(function (code) {
          return code !== element.currentLanguage;
        })[0];

        element._render({
          countryMismatch: false,
          languageMismatch: Boolean(other),
          targetCountry: element.currentCountry,
          targetLanguage: other || element.currentLanguage,
          countryNames: {}
        });
      });

      document.addEventListener('shopify:section:deselect', function (event) {
        if (event.target === section) {
          element.hidden = true;
        }
      });
    }
  }

  window.customElements.define('localization-banner', LocalizationBanner);
})();
